<template>
  <div class="taskbar">
    <!-- Start Menu Button -->
    <div class="flex items-center space-x-2 cursor-pointer" @click="toggleApplications">
      <svg
        class="w-6 h-6 text-black"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 6h16M4 12h16M4 18h16"
        ></path>
      </svg>
      <span class="text-black hidden sm:inline">Applications</span>
    </div>

    <!-- Open Windows -->
    <div class="flex-1 flex items-center space-x-4 overflow-x-auto px-4">
      <div
        v-for="window in openWindows"
        :key="window.id"
        class="flex items-center space-x-2 cursor-pointer px-3 py-1 rounded hover:bg-gray-200"
        :class="{ 'bg-gray-200': window.isOpen }"
        @click="handleWindowClick(window)"
      >
        <svg
          class="w-5 h-5 text-black"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
          ></path>
        </svg>
        <span class="text-sm text-black hidden sm:inline">{{ window.name }}</span>
      </div>

      <div
        v-for="doc in openDocuments"
        :key="doc.slug"
        class="flex items-center space-x-2 cursor-pointer px-3 py-1 rounded hover:bg-gray-200"
        :class="{ 'bg-gray-200': doc.isOpen }"
        @click="handleDocumentClick(doc)"
      >
        <svg
          class="w-5 h-5 text-black"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          ></path>
        </svg>
        <span class="text-sm text-black hidden sm:inline">{{ doc.name }}</span>
      </div>
    </div>

    <!-- Mobile Menu Button -->
    <div class="sm:hidden relative">
      <button 
        class="p-2 hover:bg-gray-200 rounded"
        @click.stop="toggleMobileMenu"
      >
        <svg
          class="w-6 h-6 text-black"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
          ></path>
        </svg>
      </button>

      <!-- Mobile Menu -->
      <div 
        v-if="isMobileMenuOpen"
        class="absolute bottom-full right-2 mb-2 bg-white rounded-lg shadow-lg py-2 min-w-[200px] z-[1001]"
        @click.stop
      >
        <div
          v-for="window in openWindows"
          :key="window.id"
          class="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center space-x-2"
          @click="handleWindowClick(window)"
        >
          <svg class="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
          </svg>
          <span class="text-black">{{ window.name }}</span>
        </div>
        <div
          v-for="doc in openDocuments"
          :key="doc.slug"
          class="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center space-x-2"
          @click="handleDocumentClick(doc)"
        >
          <svg class="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <span class="text-black">{{ doc.name }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppTaskbar',
  props: {
    openWindows: {
      type: Array,
      required: true
    },
    openDocuments: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isMobileMenuOpen: false
    };
  },
  mounted() {
    // Close mobile menu when clicking outside
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    handleOutsideClick(event) {
      if (this.isMobileMenuOpen && !event.target.closest('.sm\\:hidden')) {
        this.isMobileMenuOpen = false;
      }
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    },
    handleWindowClick(window) {
      this.isMobileMenuOpen = false;
      this.$emit('window-click', window);
    },
    handleDocumentClick(doc) {
      this.isMobileMenuOpen = false;
      this.$emit('document-click', doc);
    },
    toggleApplications() {
      this.$emit('toggle-applications');
    }
  }
};
</script>

<style scoped>
.taskbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  color: #000000;
  z-index: 1000;
}

/* Add these styles */
@media (max-width: 936px) {
  .taskbar {
    position: fixed;
    bottom: 0;
  }
}
</style>
